/**
 * ConfidenceSurvey — 1–5 confidence rating modal.
 *
 * Fires after each lesson complete, after a failed quiz, and as an occasional
 * Dashboard check-in. Low ratings (≤ 2) surface a "Get help from the AI
 * Teacher" CTA that hands off to /lms/planner/chat with the lesson context
 * baked in via the URL query string.
 *
 * Props:
 *   open, onClose
 *   trigger:      "lesson_complete" | "quiz_failed" | "dashboard_checkin"
 *   context:      { lesson_id?, course_id?, quiz_id?, lesson_title?, course_title? }
 *   API:          `${REACT_APP_BACKEND_URL}/api`
 *   onSubmit?:    optional callback fired after the POST resolves
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { X, Sparkles, ChevronRight } from 'lucide-react';

const FACE_MAP = [
  { value: 1, label: 'Lost',            emoji: '😖', tone: 'text-rose-600'    },
  { value: 2, label: 'Shaky',           emoji: '😕', tone: 'text-orange-600'  },
  { value: 3, label: 'Getting there',   emoji: '🙂', tone: 'text-amber-600'   },
  { value: 4, label: 'Solid',           emoji: '😃', tone: 'text-emerald-600' },
  { value: 5, label: 'Confident',       emoji: '🤩', tone: 'text-primary'     },
];

const TRIGGER_COPY = {
  lesson_complete: {
    eyebrow: 'Quick check-in',
    title:   'How solid do you feel on this material?',
    subtitle:'A one-tap rating helps us tune the next lesson (and get the AI Teacher involved if you need a hand).',
  },
  quiz_failed: {
    eyebrow: 'That quiz was tricky',
    title:   'Where\u2019s your confidence right now?',
    subtitle:'Rate yourself honestly. If you\u2019re below the line, the AI Teacher can pinpoint what to review before you retake.',
  },
  dashboard_checkin: {
    eyebrow: 'How are you doing?',
    title:   'Feeling good about your progress?',
    subtitle:'A quick self-rating keeps your learning path honest. If you\u2019re stuck anywhere, we\u2019ll route you to help.',
  },
};

const ConfidenceSurvey = ({ open, onClose, trigger, context = {}, API, onSubmit }) => {
  const navigate = useNavigate();
  const [value, setValue] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {struggling, planner_prompt}

  if (!open) return null;
  const copy = TRIGGER_COPY[trigger] || TRIGGER_COPY.lesson_complete;

  const submit = async (wantsAiHelp = false) => {
    if (!value) {
      toast.info('Pick a rating first');
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${API}/lms/confidence`, {
        trigger,
        confidence: value,
        notes: notes.trim() || undefined,
        lesson_id: context.lesson_id,
        course_id: context.course_id,
        quiz_id:   context.quiz_id,
        wants_ai_help: wantsAiHelp,
      });
      setResult(data);
      onSubmit && onSubmit(data);
      if (!data.struggling) {
        toast.success('Thanks — logged.');
        onClose && onClose();
      }
    } catch (err) {
      console.error('confidence submit failed', err);
      toast.error('Could not save your rating');
    } finally {
      setSubmitting(false);
    }
  };

  const goToAITeacher = () => {
    // Route to /learning with a query param the LearningCatalog will pick up
    // to auto-open the planner dialog with the pre-filled struggling prompt.
    const payload = encodeURIComponent(result?.planner_prompt || '');
    navigate(`/learning?ai_help=${payload}`);
    onClose && onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-navy-950/70 backdrop-blur-sm px-4"
      data-testid="confidence-survey-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
          aria-label="Close"
          data-testid="confidence-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
          {copy.eyebrow}
        </div>
        <h2 className="mt-1 font-heading text-lg font-extrabold text-foreground leading-snug">
          {copy.title}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{copy.subtitle}</p>

        {(context.lesson_title || context.course_title) && (
          <div className="mt-3 rounded-lg bg-muted/60 border border-border px-3 py-2 text-xs">
            {context.lesson_title && (
              <div className="font-semibold text-foreground truncate">{context.lesson_title}</div>
            )}
            {context.course_title && (
              <div className="text-muted-foreground truncate">{context.course_title}</div>
            )}
          </div>
        )}

        {/* Face rating row */}
        <div className="mt-5 grid grid-cols-5 gap-2" role="radiogroup" aria-label="Confidence rating">
          {FACE_MAP.map((f) => {
            const active = value === f.value;
            return (
              <button
                key={f.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setValue(f.value)}
                data-testid={`confidence-rating-${f.value}`}
                className={`rounded-xl border p-2 flex flex-col items-center gap-1 transition-all ${
                  active
                    ? 'border-primary bg-primary/10 shadow-sm scale-105'
                    : 'border-border hover:border-primary/60 hover:bg-muted'
                }`}
              >
                <span className={`text-2xl ${f.tone}`} aria-hidden="true">{f.emoji}</span>
                <span className="text-[10px] font-bold uppercase tracking-wide text-foreground/80">{f.label}</span>
              </button>
            );
          })}
        </div>

        {/* Notes — optional */}
        {!result && (
          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground" htmlFor="confidence-notes">
              What{'\u2019'}s tripping you up? (optional)
            </label>
            <textarea
              id="confidence-notes"
              data-testid="confidence-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. shelf temps, moisture math, packaging..."
              className="mt-1 w-full min-h-[64px] rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/60"
            />
          </div>
        )}

        {/* Struggling → AI Teacher CTA */}
        {result?.struggling && (
          <div className="mt-4 rounded-xl border border-primary/40 bg-primary/5 p-4">
            <div className="flex items-start gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="font-heading text-sm font-extrabold text-foreground">
                  Let the AI Teacher point the way
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  We{'\u2019'}ll drop you into the Planner with your context so it can recommend the exact next lessons + one action to close the gap.
                </p>
                <Button
                  size="sm"
                  onClick={goToAITeacher}
                  data-testid="confidence-open-ai-teacher"
                  className="mt-3 rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Talk to the AI Teacher <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        {!result && (
          <div className="mt-5 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-xs font-semibold text-muted-foreground hover:text-foreground underline underline-offset-2"
              data-testid="confidence-skip-btn"
            >
              Skip for now
            </button>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => submit(true)}
                disabled={submitting || !value}
                className="rounded-full font-bold"
                data-testid="confidence-help-btn"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1" /> I need help
              </Button>
              <Button
                size="sm"
                onClick={() => submit(false)}
                disabled={submitting || !value}
                className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="confidence-submit-btn"
              >
                {submitting ? 'Saving\u2026' : 'Submit'}
              </Button>
            </div>
          </div>
        )}

        {result && !result.struggling && (
          <div className="mt-5 flex items-center justify-end">
            <Button size="sm" onClick={onClose} className="rounded-full font-bold" data-testid="confidence-done-btn">
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfidenceSurvey;
